import { useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import {
  PencilIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';

import { useAppDispatch } from 'src/app/store/hooks';
import { DeleteModal } from 'src/common/DeleteModal';
import {
  TripSubscriptionListRead,
  useLazyPassengersRetrieveQuery,
  useLazyTripsSubscriptionsRetrieveQuery,
  useTripsSubscriptionsDestroyMutation,
} from 'src/common/external/bambi-api/bambiApi';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import {
  setSelectedPayer,
  setSelectedTripSubscription,
  setShowTripSubscriptionModal,
} from '../trip-subscriptions.slice';

export const RowActions = ({
  tripSubscription,
}: {
  tripSubscription: TripSubscriptionListRead;
}) => {
  const { isAdmin, isOwner, isBambi } = useUserRoles();
  const canDelete = isOwner || isAdmin || isBambi;
  const dispatch = useAppDispatch();

  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [deleteTripSubscription] = useTripsSubscriptionsDestroyMutation();
  const [fetchPassenger] = useLazyPassengersRetrieveQuery();
  const [fetchSubscription] = useLazyTripsSubscriptionsRetrieveQuery();

  const handleViewEditSubscription = async () => {
    if (!tripSubscription.passenger.id) return; // this should not happen

    const passenger = await fetchPassenger({
      id: tripSubscription.passenger.id,
      deletedObjects: 'include',
    }).unwrap();

    const subscription = await fetchSubscription({
      id: tripSubscription.id,
    }).unwrap();

    dispatch(
      setSelectedTripSubscription({
        tripSubscription: subscription,
        passenger,
      })
    );
    dispatch(setSelectedPayer(subscription.payer));
    dispatch(setShowTripSubscriptionModal(true));
  };

  const onEndTripSubscription = async (isDelete: boolean) => {
    setIsSubmitting(true);
    setError(undefined);
    try {
      await deleteTripSubscription({
        id: tripSubscription.id,
        delete: isDelete,
      }).unwrap();
      setIsConfirmingCancel(false);
      setIsConfirmingDelete(false);
    } catch (error) {
      const formattedError = formatServerError(error);
      setError(formattedError);
    }
    setIsSubmitting(false);
  };

  const endingStartDate = DateTime.now()
    .plus({ days: 1 })
    .toLocaleString(DateTime.DATE_SHORT);

  return (
    <div className="flex flex-row items-center justify-start">
      <Button
        dataTestId="view-edit-trip-subscription-action"
        variant="icon"
        ariaLabel="Edit Trip Subscription"
        onClick={handleViewEditSubscription}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <PencilIcon className="mt-1 h-[20px] w-[24px]" />
          Edit
        </div>
      </Button>
      <Button
        dataTestId="trip-subscription-cancel-action"
        variant="icon"
        ariaLabel="Cancel Trip Subscription"
        onClick={() => {
          setError(undefined);
          setIsConfirmingCancel(true);
        }}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <XCircleIcon className="mt-1 h-[20px] w-[24px]" />
          Cancel
        </div>
      </Button>
      {canDelete ? (
        <Button
          dataTestId="trip-subscription-delete-action"
          variant="icon"
          ariaLabel="Delete Trip Subscription"
          onClick={() => {
            setError(undefined);
            setIsConfirmingDelete(true);
          }}
        >
          <div
            className="flex flex-col items-center text-xs"
            style={{ fontSize: '0.625rem' }}
          >
            <TrashIcon className="mt-1 h-[20px] w-[24px]" />
            Delete
          </div>
        </Button>
      ) : null}
      <DeleteModal
        open={isConfirmingCancel || isConfirmingDelete}
        setOpen={(val) => {
          setIsConfirmingCancel(val);
          setIsConfirmingDelete(val);
        }}
        title="End Trip Subscription"
        confirmText={isConfirmingDelete ? 'Delete ' : 'Confirm'}
        description={`Are you sure you want to end this trip subscription?  
        Any unassigned trip associated with this subscription and a scheduled pickup
        at ${endingStartDate} or after will be ${
          isConfirmingDelete ? 'deleted' : 'canceled'
        }.`}
        onCancel={() => {
          setIsConfirmingCancel(false);
          setIsConfirmingDelete(false);
        }}
        onConfirm={() => onEndTripSubscription(isConfirmingDelete)}
        error={error}
        loading={isSubmitting}
      />
    </div>
  );
};
